<script setup>
import {
  ref,
  reactive,
  computed,
  onMounted,
  defineExpose,
  nextTick,
} from "vue";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useTaskStore } from "@/stores/taskStore";
import { useProjectStore } from "@/stores/projectStore";
import { useUserStore } from "@/stores/userStore";
import { toast } from "@/utils/toast";
import { Modal } from "bootstrap";

defineOptions({ name: "TasksModal" });

const taskStore = useTaskStore();
const projectStore = useProjectStore();
const userStore = useUserStore();

const editor = ClassicEditor;

const emit = defineEmits(["saved"]);

/* -------- modal bookkeeping -------- */
const isEdit = ref(false);
const editingId = ref(null);
const saving = ref(false);

const deletingId = ref(null);
const deleting = computed(() => taskStore.deletingId === deletingId.value);
function openForDelete(id) {
  deletingId.value = id || null;
  showModal("delete_task");
}

/* -------- form -------- */
const form = reactive({
  title: "",
  project_id: null,
  due_date: "",
  status: "Pending",
  priority: "Medium",
  visibility: "Private",
  tags: [],
  description: "",
  assignee_ids: [],

  progress: 0,
  subtasks: [],
  board_id: null,
});

const autoFromSubtasks = ref(true);

/* -------- options -------- */
const projectOptions = computed(() =>
  (projectStore.items || []).map((p) => ({ id: p.id, label: p.name }))
);

const userOptions = ref([]); // {id,label,avatar?}

/* -------- users load -------- */
async function fetchUsers(q = "") {
  try {
    const rows =
      (await userStore.list?.({ q, per_page: 50 })) ||
      (await userStore.fetchAll?.({ q, per_page: 50 }));

    const arr =
      rows?.data?.data ||
      rows?.data ||
      userStore.items?.value ||
      userStore.items ||
      [];

    userOptions.value = (arr || []).map((u) => ({
      id: u.id,
      label:
        u.name ||
        [u.first_name, u.last_name].filter(Boolean).join(" ") ||
        u.email ||
        `User #${u.id}`,
      avatar: u.avatar_url || u.avatar || null,
    }));
  } catch (e) {
    toast(
      e?.response?.data?.message || e.message || "Failed to load users",
      "error"
    );
  }
}

/* -------- open/close helpers -------- */
function showModal(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const inst =
    Modal.getInstance(el) ||
    new Modal(el, { backdrop: "static", keyboard: true, focus: true });
  inst.show();
}
function hideModal(id) {
  const el = document.getElementById(id);
  const inst = el ? Modal.getInstance(el) : null;
  inst?.hide();
}

/* -------- normalize subtasks from API -------- */
function normalizeSubtasks(list) {
  const arr = Array.isArray(list) ? list : [];
  return arr
    .map((s, i) => ({
      id: s.id ?? null,
      title: s.title ?? "",
      is_done: !!s.is_done,
      weight: Number.isFinite(s.weight) ? s.weight : 0,
      position: Number.isFinite(s.position) ? s.position : i,
    }))
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

/* -------- computed progress from subtasks -------- */
const computedFromSubtasks = computed(() => {
  if (!form.subtasks.length) return null;

  const totalWeight = form.subtasks.reduce(
    (sum, s) => sum + (s.weight || 0),
    0
  );

  if (totalWeight > 0) {
    const done = form.subtasks
      .filter((s) => s.is_done)
      .reduce((sum, s) => sum + (s.weight || 0), 0);
    return Math.round((done / Math.max(1, totalWeight)) * 100);
  }

  const total = form.subtasks.length;
  const done = form.subtasks.filter((s) => s.is_done).length;
  return Math.round((done / Math.max(1, total)) * 100);
});

const effectiveProgress = computed(() => {
  if (form.subtasks.length && autoFromSubtasks.value)
    return computedFromSubtasks.value ?? 0;
  return Number.isFinite(form.progress) ? form.progress : 0;
});

/* -------- expose openCreate/openEdit -------- */
function applyDefaults(d = {}) {
  if (d.status) form.status = d.status;
  if (d.board_id != null) form.board_id = d.board_id;
}

function resetForm() {
  Object.assign(form, {
    title: "",
    project_id: null,
    due_date: "",
    status: "Pending",
    priority: "Medium",
    visibility: "Private",
    tags: [],
    description: "",
    assignee_ids: [],
    progress: 0,
    subtasks: [],
    board_id: null,
  });
  autoFromSubtasks.value = true;
  assigneeQuery.value = "";
  tagDraft.value = "";
  ddProjectOpen.value = false;
  ddAssigneeOpen.value = false;
}

function openCreate(defaults = {}) {
  isEdit.value = false;
  editingId.value = null;
  resetForm();
  applyDefaults(defaults);
  showModal("add_task");

  nextTick(() => titleRef.value?.focus?.());
}

async function openEdit(id) {
  try {
    const data = await taskStore.get(id);
    isEdit.value = true;
    editingId.value = id;

    Object.assign(form, {
      title: data.title || "",
      project_id: data.project_id || null,
      due_date: data.due_date || "",
      status: data.status || "Pending",
      priority: data.priority || "Medium",
      visibility: data.visibility || "Private",
      tags: Array.isArray(data.tags) ? data.tags : [],
      description: data.description || "",
      assignee_ids: Array.isArray(data.assignee_ids) ? data.assignee_ids : [],
      progress: Number.isFinite(data.progress) ? data.progress : 0,
      subtasks: normalizeSubtasks(data.subtasks),
      board_id: data.board_id ?? null,
    });

    autoFromSubtasks.value = form.subtasks.length > 0;

    tagDraft.value = (form.tags || []).join(", ");
    showModal("add_task");

    nextTick(() => titleRef.value?.focus?.());
  } catch (e) {
    toast(
      e?.response?.data?.message || e.message || "Failed to open task",
      "error"
    );
  }
}

/* -------- build payload -------- */
function buildPayload() {
  const payload = {
    title: form.title,
    project_id: form.project_id,
    due_date: form.due_date || null,
    status: form.status,
    priority: form.priority,
    visibility: form.visibility,
    tags: form.tags,
    description: form.description,
    assignee_ids: form.assignee_ids,
  };

  if (form.board_id != null) payload.board_id = form.board_id;

  if (form.subtasks.length && autoFromSubtasks.value) {
    payload.progress = computedFromSubtasks.value ?? 0;
  } else {
    payload.progress = Number.isFinite(form.progress) ? form.progress : 0;
  }

  if (form.subtasks.length) {
    payload.subtasks = form.subtasks.map((s, i) => ({
      id: s.id || undefined,
      title: s.title,
      is_done: !!s.is_done,
      weight: Number.isFinite(s.weight) ? s.weight : 0,
      position: Number.isFinite(s.position) ? s.position : i,
    }));
  }

  return payload;
}

/* -------- submit -------- */
async function submit() {
  if (!form.title?.trim()) return toast("Title is required", "error");

  try {
    saving.value = true;
    const payload = buildPayload();

    if (isEdit.value && editingId.value)
      await taskStore.update(editingId.value, payload);
    else await taskStore.create(payload);

    emit("saved");
    hideModal("add_task");
    toast(isEdit.value ? "Task updated" : "Task created", "success");
  } catch (e) {
    toast(e?.response?.data?.message || e.message || "Save failed", "error");
  } finally {
    saving.value = false;
  }
}

/* -------- delete confirm -------- */
async function confirmDelete() {
  if (!deletingId.value) return;

  try {
    await taskStore.remove(deletingId.value);
    hideModal("delete_task");
    toast("Task deleted", "success");
    deletingId.value = null;
    emit("saved");
  } catch (e) {
    toast(e?.response?.data?.message || e.message || "Delete failed", "error");
  }
}

/* -------- Assignees premium dropdown -------- */
const ddAssigneeOpen = ref(false);
const assigneeQuery = ref("");
const assigneeInputRef = ref(null);

const filteredUsers = computed(() => {
  const q = assigneeQuery.value.trim().toLowerCase();
  if (!q) return userOptions.value;
  return userOptions.value.filter((u) =>
    (u.label || "").toLowerCase().includes(q)
  );
});

function toggleAssignee(id) {
  const i = form.assignee_ids.indexOf(id);
  if (i === -1) form.assignee_ids.push(id);
  else form.assignee_ids.splice(i, 1);
}
function isChecked(id) {
  return form.assignee_ids.includes(id);
}
const selectedAssignees = computed(() =>
  userOptions.value.filter((u) => form.assignee_ids.includes(u.id))
);

/* -------- Project dropdown (premium) -------- */
const ddProjectOpen = ref(false);
const projectQuery = ref("");
const projectInputRef = ref(null);

const filteredProjects = computed(() => {
  const q = projectQuery.value.trim().toLowerCase();
  if (!q) return projectOptions.value;
  return projectOptions.value.filter((p) =>
    (p.label || "").toLowerCase().includes(q)
  );
});

function chooseProject(p) {
  form.project_id = p?.id ?? null;
  ddProjectOpen.value = false;
}

/* -------- Tags premium input -------- */
const tagDraft = ref("");
function syncTagsFromDraft() {
  form.tags = (tagDraft.value || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

/* -------- Subtasks editor -------- */
function addSubtask() {
  form.subtasks.push({
    id: null,
    title: "",
    is_done: false,
    weight: 0,
    position: form.subtasks.length,
  });
  nextTick(() => {
    const last = document.querySelector(".dq-subtask-title-last");
    last?.focus?.();
  });
}
function removeSubtask(idx) {
  form.subtasks.splice(idx, 1);
  form.subtasks.forEach((s, i) => (s.position = i));
}
function moveUp(idx) {
  if (idx <= 0) return;
  const [it] = form.subtasks.splice(idx, 1);
  form.subtasks.splice(idx - 1, 0, it);
  form.subtasks.forEach((s, i) => (s.position = i));
}
function moveDown(idx) {
  if (idx >= form.subtasks.length - 1) return;
  const [it] = form.subtasks.splice(idx, 1);
  form.subtasks.splice(idx + 1, 0, it);
  form.subtasks.forEach((s, i) => (s.position = i));
}

/* -------- UX helpers -------- */
function priorityBadge(p) {
  const s = String(p || "").toLowerCase();
  if (s === "high") return "dq-pill dq-pill-danger";
  if (s === "low") return "dq-pill dq-pill-slate";
  return "dq-pill dq-pill-amber";
}
function statusBadge(s) {
  const v = String(s || "").toLowerCase();
  if (v === "completed") return "dq-pill dq-pill-success";
  if (v === "inprogress") return "dq-pill dq-pill-primary";
  if (v === "onhold") return "dq-pill dq-pill-slate";
  return "dq-pill dq-pill-slate";
}

const titleRef = ref(null);

/* click-out close for custom dropdowns */
function onDocClick(e) {
  const inDd = e.target?.closest?.(".dq-dd");
  if (!inDd) {
    ddProjectOpen.value = false;
    ddAssigneeOpen.value = false;
  }
}

/* -------- mount -------- */
onMounted(async () => {
  document.addEventListener("click", onDocClick);
  if (!projectStore.items?.length) await projectStore.list();
  await fetchUsers("");
});

defineExpose({ openCreate, openEdit, openForDelete, confirmDelete });
</script>

<template>
  <!-- Add / Edit Task Modal -->
  <div class="modal fade" id="add_task" tabindex="-1" aria-hidden="true">
    <!-- ✅ no modal-dialog-centered (better scroll behavior) -->
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content dq-modal">
        <!-- Header -->
        <div class="modal-header dq-header">
          <div class="dq-header-left">
            <div class="dq-icon">
              <i class="ti" :class="isEdit ? 'ti-pencil' : 'ti-plus'"></i>
            </div>
            <div>
              <h4 class="modal-title mb-0">
                {{ isEdit ? "Edit Task" : "Create Task" }}
              </h4>
              <div class="dq-sub">
                Plan work, assign people, track progress — fast.
              </div>

              <div
                class="dq-meta mt-2"
                v-if="isEdit || form.status || form.priority"
              >
                <span :class="statusBadge(form.status)">{{ form.status }}</span>
                <span :class="priorityBadge(form.priority)">{{
                  form.priority
                }}</span>
                <span class="dq-pill dq-pill-slate">{{ form.visibility }}</span>
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submit">
          <!-- ✅ Scroll body -->
          <div class="modal-body dq-body">
            <div class="dq-layout">
              <!-- LEFT -->
              <div class="dq-main">
                <!-- Core -->
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <span class="dq-badge dq-badge-blue"
                        ><i class="ti ti-list-details"></i
                      ></span>
                      <div>
                        <div class="dq-h">Task Details</div>
                        <div class="dq-sub">
                          Title, project, due date, status & priority.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <div class="dq-field">
                      <label class="dq-label"
                        >Title <span class="text-danger">*</span></label
                      >
                      <input
                        ref="titleRef"
                        v-model.trim="form.title"
                        type="text"
                        class="form-control dq-input"
                        placeholder="e.g. Prepare payroll runs for December"
                        required
                      />
                      <div class="dq-help">
                        Make it actionable and specific.
                      </div>
                    </div>

                    <div class="dq-grid-2 mt-2">
                      <div class="dq-field">
                        <label class="dq-label">Due Date</label>
                        <div class="dq-date">
                          <span class="dq-date-ic"
                            ><i class="ti ti-calendar"></i
                          ></span>
                          <input
                            v-model="form.due_date"
                            type="date"
                            class="form-control dq-input dq-date-input"
                          />
                        </div>
                      </div>

                      <div class="dq-field">
                        <label class="dq-label">Project</label>

                        <div class="dq-dd">
                          <button
                            type="button"
                            class="dq-dd-btn"
                            @click="
                              ddProjectOpen = !ddProjectOpen;
                              ddAssigneeOpen = false;
                              nextTick(() => projectInputRef?.focus?.());
                            "
                          >
                            <span class="dq-dd-text">
                              {{
                                projectOptions.find(
                                  (p) => p.id === form.project_id
                                )?.label || "Select project"
                              }}
                            </span>
                            <i class="ti ti-chevron-down"></i>
                          </button>

                          <div
                            v-if="ddProjectOpen"
                            class="dq-dd-menu"
                            @click.stop
                          >
                            <div class="dq-dd-search">
                              <i class="ti ti-search"></i>
                              <input
                                ref="projectInputRef"
                                v-model="projectQuery"
                                class="dq-dd-input"
                                type="text"
                                placeholder="Search projects…"
                                autocomplete="off"
                              />
                            </div>

                            <div class="dq-dd-list">
                              <button
                                v-for="p in filteredProjects"
                                :key="p.id"
                                type="button"
                                class="dq-dd-item"
                                :class="
                                  p.id === form.project_id ? 'is-selected' : ''
                                "
                                @click="chooseProject(p)"
                              >
                                <div class="dq-dd-item-main">
                                  <div class="dq-dd-item-title">
                                    {{ p.label }}
                                  </div>
                                </div>
                                <i
                                  v-if="p.id === form.project_id"
                                  class="ti ti-check"
                                ></i>
                              </button>

                              <div
                                v-if="!filteredProjects.length"
                                class="dq-dd-empty"
                              >
                                No projects found
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="dq-help">Optional.</div>
                      </div>
                    </div>

                    <div class="dq-grid-3 mt-2">
                      <div class="dq-field">
                        <label class="dq-label">Status</label>

                        <vue-select
                          v-model="form.status"
                          :options="[
                            'Pending',
                            'Inprogress',
                            'Completed',
                            'Onhold',
                          ]"
                          placeholder="Select Status"
                          class="dq-vselect"
                          :clearable="false"
                        />
                      </div>

                      <div class="dq-field">
                        <label class="dq-label">Priority</label>

                        <vue-select
                          v-model="form.priority"
                          :options="['High', 'Medium', 'Low']"
                          placeholder="Select Status"
                          class="dq-vselect"
                          :clearable="false"
                        />
                      </div>

                      <div class="dq-field">
                        <label class="dq-label">Visibility</label>
                        <div class="dq-seg">
                          <label class="dq-seg-item">
                            <input
                              type="radio"
                              value="Public"
                              v-model="form.visibility"
                            />
                            <span>Public</span>
                          </label>
                          <label class="dq-seg-item">
                            <input
                              type="radio"
                              value="Private"
                              v-model="form.visibility"
                            />
                            <span>Private</span>
                          </label>
                          <label class="dq-seg-item">
                            <input
                              type="radio"
                              value="Admin"
                              v-model="form.visibility"
                            />
                            <span>Admin</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Assignees + tags -->
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <span class="dq-badge dq-badge-slate"
                        ><i class="ti ti-users"></i
                      ></span>
                      <div>
                        <div class="dq-h">People & Tags</div>
                        <div class="dq-sub">
                          Assign teammates and add quick labels.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <div class="dq-field">
                      <label class="dq-label">Assignees</label>

                      <div class="dq-dd">
                        <button
                          type="button"
                          class="dq-dd-btn"
                          @click="
                            ddAssigneeOpen = !ddAssigneeOpen;
                            ddProjectOpen = false;
                            nextTick(() => assigneeInputRef?.focus?.());
                          "
                        >
                          <span class="dq-dd-text">
                            <template v-if="form.assignee_ids.length">
                              {{ form.assignee_ids.length }} selected
                            </template>
                            <template v-else>Select members</template>
                          </span>
                          <i class="ti ti-chevron-down"></i>
                        </button>

                        <div
                          v-if="ddAssigneeOpen"
                          class="dq-dd-menu"
                          @click.stop
                        >
                          <div class="dq-dd-search">
                            <i class="ti ti-search"></i>
                            <input
                              ref="assigneeInputRef"
                              v-model="assigneeQuery"
                              class="dq-dd-input"
                              type="text"
                              placeholder="Search users…"
                              autocomplete="off"
                            />
                          </div>

                          <div class="dq-dd-list dq-dd-list-lg">
                            <button
                              v-for="u in filteredUsers"
                              :key="u.id"
                              type="button"
                              class="dq-dd-item"
                              :class="isChecked(u.id) ? 'is-selected' : ''"
                              @click="toggleAssignee(u.id)"
                            >
                              <div class="dq-dd-item-main dq-user">
                                <div class="dq-ava">
                                  <img
                                    v-if="u.avatar"
                                    :src="u.avatar"
                                    alt="avatar"
                                  />
                                  <i v-else class="ti ti-user"></i>
                                </div>
                                <div class="dq-dd-item-main">
                                  <div class="dq-dd-item-title">
                                    {{ u.label }}
                                  </div>
                                  <div class="dq-dd-item-sub">
                                    Click to
                                    {{ isChecked(u.id) ? "remove" : "add" }}
                                  </div>
                                </div>
                              </div>
                              <i v-if="isChecked(u.id)" class="ti ti-check"></i>
                            </button>

                            <div
                              v-if="!filteredUsers.length"
                              class="dq-dd-empty"
                            >
                              No users found
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        class="dq-chips mt-2"
                        v-if="selectedAssignees.length"
                      >
                        <span
                          v-for="u in selectedAssignees"
                          :key="u.id"
                          class="dq-chip"
                        >
                          <span class="dq-chip-ava">
                            <img v-if="u.avatar" :src="u.avatar" alt="avatar" />
                            <i v-else class="ti ti-user"></i>
                          </span>
                          <span class="dq-chip-t">{{ u.label }}</span>
                          <button
                            type="button"
                            class="dq-chip-x"
                            @click="toggleAssignee(u.id)"
                            aria-label="remove"
                          >
                            <i class="ti ti-x"></i>
                          </button>
                        </span>
                      </div>

                      <div class="dq-help">Assigning helps accountability.</div>
                    </div>

                    <div class="dq-field mt-2">
                      <label class="dq-label">Tags</label>
                      <input
                        v-model="tagDraft"
                        @input="syncTagsFromDraft"
                        type="text"
                        class="form-control dq-input"
                        placeholder="Comma separated e.g. Internal, Meeting, Payroll"
                      />
                      <div class="dq-chips mt-2" v-if="form.tags.length">
                        <span
                          v-for="t in form.tags"
                          :key="t"
                          class="dq-chip dq-chip-tag"
                        >
                          <i class="ti ti-hash"></i>
                          <span class="dq-chip-t">{{ t }}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Progress + checklist -->
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <span class="dq-badge dq-badge-amber"
                        ><i class="ti ti-progress"></i
                      ></span>
                      <div>
                        <div class="dq-h">Progress & Checklist</div>
                        <div class="dq-sub">
                          Use checklist to auto-calculate progress.
                        </div>
                      </div>
                    </div>

                    <div class="dq-switch">
                      <input
                        id="autoFromSubtasks"
                        class="dq-switch-input"
                        type="checkbox"
                        v-model="autoFromSubtasks"
                        :disabled="!form.subtasks.length"
                      />
                      <label class="dq-switch-label" for="autoFromSubtasks">
                        Auto
                      </label>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <!-- progress meter -->
                    <div class="dq-progress">
                      <div class="dq-progress-top">
                        <div class="dq-k">Progress</div>
                        <div class="dq-v">{{ effectiveProgress }}%</div>
                      </div>

                      <div class="dq-bar">
                        <div
                          class="dq-bar-fill"
                          :style="{ width: effectiveProgress + '%' }"
                        ></div>
                      </div>

                      <div class="dq-progress-controls mt-2">
                        <input
                          type="range"
                          class="form-range"
                          min="0"
                          max="100"
                          step="1"
                          v-model.number="form.progress"
                          :disabled="
                            autoFromSubtasks && form.subtasks.length > 0
                          "
                        />
                        <input
                          type="number"
                          class="form-control dq-input dq-w-110"
                          min="0"
                          max="100"
                          v-model.number="form.progress"
                          :disabled="
                            autoFromSubtasks && form.subtasks.length > 0
                          "
                        />
                        <span
                          v-if="autoFromSubtasks && form.subtasks.length"
                          class="dq-pill dq-pill-slate"
                        >
                          From checklist
                        </span>
                      </div>
                    </div>

                    <!-- checklist -->
                    <div class="dq-checklist mt-3">
                      <div
                        class="d-flex align-items-center justify-content-between"
                      >
                        <div class="dq-h-sm">Checklist</div>
                        <button
                          type="button"
                          class="btn btn-light dq-mini-btn"
                          @click="addSubtask"
                        >
                          <i class="ti ti-plus me-1"></i>Add item
                        </button>
                      </div>

                      <div v-if="!form.subtasks.length" class="dq-empty">
                        No checklist items — add a few steps for better progress
                        tracking.
                      </div>

                      <div
                        v-for="(s, idx) in form.subtasks"
                        :key="idx"
                        class="dq-subtask"
                      >
                        <div class="dq-subtask-left">
                          <input
                            type="checkbox"
                            class="form-check-input"
                            v-model="s.is_done"
                          />
                          <input
                            v-model.trim="s.title"
                            type="text"
                            class="form-control dq-input"
                            :class="
                              idx === form.subtasks.length - 1
                                ? 'dq-subtask-title-last'
                                : ''
                            "
                            placeholder="e.g. Confirm payroll additions"
                          />
                        </div>

                        <div class="dq-subtask-right">
                          <div class="dq-weight">
                            <span class="dq-k">W</span>
                            <input
                              v-model.number="s.weight"
                              type="number"
                              class="form-control dq-input dq-w-90"
                              min="0"
                              max="100"
                            />
                          </div>

                          <div class="dq-btns">
                            <button
                              type="button"
                              class="btn btn-light dq-icon-btn"
                              @click="moveUp(idx)"
                              :disabled="idx === 0"
                              title="Move up"
                            >
                              <i class="ti ti-arrow-up"></i>
                            </button>
                            <button
                              type="button"
                              class="btn btn-light dq-icon-btn"
                              @click="moveDown(idx)"
                              :disabled="idx === form.subtasks.length - 1"
                              title="Move down"
                            >
                              <i class="ti ti-arrow-down"></i>
                            </button>
                            <button
                              type="button"
                              class="btn btn-danger dq-icon-btn"
                              @click="removeSubtask(idx)"
                              title="Remove"
                            >
                              <i class="ti ti-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div class="dq-help mt-2">
                        Tip: set weights (W) if some steps matter more than
                        others.
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Description -->
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <span class="dq-badge dq-badge-purple"
                        ><i class="ti ti-notes"></i
                      ></span>
                      <div>
                        <div class="dq-h">Description</div>
                        <div class="dq-sub">
                          Optional details for the assignees.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <ckeditor :editor="editor" v-model="form.description" />
                  </div>
                </div>
              </div>

              <!-- RIGHT summary -->
              <div class="dq-side">
                <div class="dq-sticky">
                  <div class="dq-card">
                    <div class="dq-card-head">
                      <div class="dq-card-title">
                        <span class="dq-badge dq-badge-dark"
                          ><i class="ti ti-sparkles"></i
                        ></span>
                        <div>
                          <div class="dq-h">Quick Summary</div>
                          <div class="dq-sub">At a glance</div>
                        </div>
                      </div>
                    </div>

                    <div class="dq-card-body">
                      <!-- Top KPIs -->
                      <div class="dq-kpi-grid">
                        <div class="dq-kpi">
                          <div class="dq-kpi-top">
                            <span class="dq-kpi-ic dq-kpi-ic-blue"
                              ><i class="ti ti-progress"></i
                            ></span>
                            <span class="dq-kpi-label">Progress</span>
                          </div>
                          <div class="dq-kpi-val">{{ effectiveProgress }}%</div>
                        </div>

                        <div class="dq-kpi">
                          <div class="dq-kpi-top">
                            <span class="dq-kpi-ic dq-kpi-ic-green"
                              ><i class="ti ti-checklist"></i
                            ></span>
                            <span class="dq-kpi-label">Checklist</span>
                          </div>
                          <div class="dq-kpi-val">
                            {{
                              form.subtasks.filter((s) => s.is_done).length
                            }}/{{ form.subtasks.length || 0 }}
                          </div>
                        </div>
                      </div>

                      <div class="dq-divider"></div>

                      <!-- Details (clean rows) -->
                      <div class="dq-kv">
                        <div class="dq-k">Project</div>
                        <div class="dq-v text-end">
                          {{
                            projectOptions.find((p) => p.id === form.project_id)
                              ?.label || "—"
                          }}
                        </div>
                      </div>

                      <div class="dq-kv">
                        <div class="dq-k">Due date</div>
                        <div class="dq-v text-end">
                          {{ form.due_date || "—" }}
                        </div>
                      </div>

                      <div class="dq-kv">
                        <div class="dq-k">Assignees</div>
                        <div class="dq-v text-end">
                          {{ form.assignee_ids.length || 0 }}
                        </div>
                      </div>

                      <div class="dq-kv">
                        <div class="dq-k">Tags</div>
                        <div class="dq-v text-end">
                          {{ form.tags.length || 0 }}
                        </div>
                      </div>

                      <div class="dq-divider"></div>

                      <!-- Status pills -->
                      <div class="dq-pill-row">
                        <span :class="statusBadge(form.status)">{{
                          form.status
                        }}</span>
                        <span :class="priorityBadge(form.priority)">{{
                          form.priority
                        }}</span>
                        <span class="dq-pill dq-pill-slate">{{
                          form.visibility
                        }}</span>
                      </div>

                      <button
                        type="submit"
                        class="btn btn-primary w-100 dq-action-btn mt-3"
                        :disabled="saving"
                      >
                        <span
                          v-if="saving"
                          class="spinner-border spinner-border-sm me-2"
                        ></span>
                        <i v-else class="ti ti-device-floppy me-2"></i>
                        {{ isEdit ? "Save Changes" : "Create Task" }}
                      </button>

                      <button
                        type="button"
                        class="btn btn-light w-100 dq-action-btn mt-2"
                        data-bs-dismiss="modal"
                      >
                        <i class="ti ti-x me-2"></i>Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">
              Cancel
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              {{ isEdit ? "Save Changes" : "Create Task" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete -->
  <div class="modal fade" id="delete_task" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content dq-modal-delete">
        <div class="modal-body text-center p-4">
          <span class="dq-del-ic">
            <i class="ti ti-trash-x"></i>
          </span>
          <h4 class="mb-1">Confirm Delete</h4>
          <p class="mb-3 text-muted">
            Are you sure you want to delete this task? This cannot be undone.
          </p>

          <div class="d-flex justify-content-center gap-2">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">
              Cancel
            </button>
            <button
              class="btn btn-danger"
              :disabled="deleting"
              @click="confirmDelete"
            >
              <span
                v-if="deleting"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
/* =========================================================
   DONEQUICK — TASKS MODAL (World-class UI + bulletproof scroll)
   IMPORTANT:
   1) Your modal-dialog MUST include: class="... dq-dialog"
   2) Keep this style section as-is (avoid duplicate scroll rules elsewhere)
   ========================================================= */

/* ========== MODAL SIZING + SCROLL (BULLETPROOF) ========== */
/* These :deep() rules MUST exist in scoped styles to control Bootstrap internals */

/* ✅ SINGLE source of truth for scrolling */
:deep(#add_task .modal-dialog) {
  height: calc(100vh - 2rem) !important;
  margin: 1rem auto !important;
}

:deep(#add_task .modal-dialog.modal-dialog-centered) {
  align-items: stretch !important; /* IMPORTANT: don't vertically center the dialog box */
}

:deep(#add_task.modal) {
  overflow: hidden !important; /* prevent outer modal from stealing scroll */
}

:deep(#add_task .modal-dialog) {
  height: calc(100vh - 2rem) !important;
  margin: 1rem auto !important;
}

:deep(#add_task .modal-content) {
  height: 100% !important;
  max-height: calc(100vh - 2rem) !important;
  display: flex !important;
  flex-direction: column !important;
  border-radius: 18px;
  overflow: hidden !important; /* keep rounded corners */
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 30px 80px rgba(16, 24, 40, 0.18);
}

:deep(#add_task .modal-body) {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
  background: rgba(248, 250, 252, 0.55);
}

:deep(#add_task .modal-header),
:deep(#add_task .modal-footer) {
  flex: 0 0 auto !important;
  background: #fff;
}

:deep(#add_task .modal-header) {
  background: linear-gradient(
    180deg,
    rgba(248, 250, 252, 1),
    rgba(255, 255, 255, 1)
  );
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

:deep(#add_task .modal-footer) {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

/* ========== HEADER ========== */
.dq-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dq-icon {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(37, 99, 235, 0.1);
  color: rgba(37, 99, 235, 1);
}

.dq-sub {
  font-size: 0.86rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 2px;
}

.dq-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* ========== LAYOUT ========== */
.dq-layout {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 14px;
  padding: 14px;
}

@media (max-width: 992px) {
  .dq-layout {
    grid-template-columns: 1fr;
  }
}

.dq-sticky {
  position: sticky;
  top: 12px;
}

@media (max-width: 992px) {
  .dq-sticky {
    position: static !important;
  }
}

/* ========== CARDS ========== */
.dq-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  box-shadow: 0 16px 40px rgba(16, 24, 40, 0.06);
  overflow: hidden;
  margin-bottom: 14px;
}

.dq-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(
    180deg,
    rgba(248, 250, 252, 1),
    rgba(255, 255, 255, 1)
  );
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.dq-card-body {
  padding: 14px 16px;
}

.dq-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dq-h {
  font-weight: 950;
  letter-spacing: -0.02em;
  color: rgba(17, 24, 39, 0.92);
}

.dq-h-sm {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.9);
}

/* ========== BADGES (ICON BUBBLES) ========== */
.dq-badge {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.dq-badge-blue {
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
}

.dq-badge-slate {
  background: rgba(148, 163, 184, 0.18);
  color: rgba(51, 65, 85, 1);
}

.dq-badge-amber {
  background: rgba(245, 158, 11, 0.14);
  color: rgba(217, 119, 6, 1);
}

.dq-badge-purple {
  background: rgba(99, 102, 241, 0.12);
  color: rgba(79, 70, 229, 1);
}

.dq-badge-dark {
  background: rgba(17, 24, 39, 0.08);
  color: rgba(17, 24, 39, 0.9);
}

/* ========== PILLS ========== */
.dq-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 0.78rem;
}

.dq-pill-primary {
  background: rgba(37, 99, 235, 0.1);
  color: rgba(37, 99, 235, 1);
}

.dq-pill-success {
  background: rgba(16, 185, 129, 0.12);
  color: rgba(5, 150, 105, 1);
}

.dq-pill-danger {
  background: rgba(220, 38, 38, 0.12);
  color: rgba(220, 38, 38, 1);
}

.dq-pill-amber {
  background: rgba(245, 158, 11, 0.14);
  color: rgba(217, 119, 6, 1);
}

.dq-pill-slate {
  background: rgba(148, 163, 184, 0.18);
  color: rgba(51, 65, 85, 1);
}

.dq-pill-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* ========== FIELDS ========== */
.dq-label {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 6px;
}

.dq-input {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
}

.dq-help {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 6px;
}

.dq-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.dq-grid-3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1.4fr;
  gap: 14px;
}

@media (max-width: 992px) {
  .dq-grid-2,
  .dq-grid-3 {
    grid-template-columns: 1fr;
  }
}

/* date icon */
.dq-date {
  position: relative;
}
.dq-date-ic {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(17, 24, 39, 0.45);
  pointer-events: none;
}
.dq-date-input {
  padding-left: 40px;
}

/* ========== SEGMENTED CONTROL (Visibility) ========== */
.dq-seg {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  overflow: hidden;
  min-height: 44px;
}
.dq-seg-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 10px;
  cursor: pointer;
  font-weight: 900;
  color: rgba(17, 24, 39, 0.78);
  background: #fff;
  user-select: none;
}
.dq-seg-item input {
  display: none;
}
.dq-seg-item:has(input:checked) {
  background: rgba(37, 99, 235, 0.08);
  color: rgba(37, 99, 235, 1);
}

/* ========== CUSTOM DROPDOWN ========== */
.dq-dd {
  position: relative;
}

.dq-dd-btn {
  width: 100%;
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  font-weight: 900;
  color: rgba(17, 24, 39, 0.85);
}

.dq-dd-text {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dq-dd-menu {
  position: absolute;
  z-index: 80;
  width: 100%;
  margin-top: 8px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  box-shadow: 0 24px 60px rgba(16, 24, 40, 0.18);
  overflow: hidden;
}

.dq-dd-search {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(248, 250, 252, 1);
  color: rgba(17, 24, 39, 0.55);
}

.dq-dd-input {
  border: 0;
  outline: none;
  background: transparent;
  width: 100%;
  font-weight: 900;
  color: rgba(17, 24, 39, 0.85);
}

.dq-dd-list {
  max-height: 280px;
  overflow: auto;
  padding: 6px;
}

.dq-dd-list-lg {
  max-height: 320px;
}

.dq-dd-item {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 10px 10px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  transition: background 0.12s ease;
}

.dq-dd-item:hover {
  background: rgba(37, 99, 235, 0.08);
}

.dq-dd-item.is-selected {
  background: rgba(16, 185, 129, 0.1);
}

.dq-dd-item-title {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.9);
}

.dq-dd-item-sub {
  font-size: 0.78rem;
  color: rgba(17, 24, 39, 0.55);
}

.dq-dd-empty {
  padding: 14px 10px;
  text-align: center;
  color: rgba(17, 24, 39, 0.55);
  font-size: 0.85rem;
}

/* user rows */
.dq-user {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dq-ava {
  width: 34px;
  height: 34px;
  border-radius: 14px;
  background: rgba(148, 163, 184, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: rgba(51, 65, 85, 1);
}
.dq-ava img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ========== CHIPS ========== */
.dq-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(248, 250, 252, 1);
  font-weight: 900;
  color: rgba(17, 24, 39, 0.85);
}

.dq-chip-ava {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(148, 163, 184, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
}

.dq-chip-ava img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dq-chip-x {
  border: 0;
  background: transparent;
  padding: 0;
  color: rgba(17, 24, 39, 0.55);
}

.dq-chip-tag {
  background: rgba(37, 99, 235, 0.06);
  border-style: dashed;
}

/* ========== PROGRESS ========== */
.dq-progress-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dq-bar {
  height: 10px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.2);
  overflow: hidden;
  margin-top: 8px;
}

.dq-bar-fill {
  height: 100%;
  background: rgba(37, 99, 235, 1);
  border-radius: 999px;
  transition: width 0.2s ease;
}

.dq-progress-controls {
  display: flex;
  gap: 10px;
  align-items: center;
}

/* ========== CHECKLIST ========== */
.dq-empty {
  margin-top: 10px;
  padding: 12px;
  border-radius: 14px;
  background: rgba(248, 250, 252, 1);
  border: 1px dashed rgba(0, 0, 0, 0.1);
  color: rgba(17, 24, 39, 0.6);
  font-weight: 800;
}

.dq-subtask {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(248, 250, 252, 0.6);
  margin-top: 10px;
}

.dq-subtask-left {
  display: flex;
  gap: 10px;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.dq-subtask-right {
  display: flex;
  gap: 10px;
  align-items: center;
}

.dq-weight {
  display: flex;
  gap: 6px;
  align-items: center;
  color: rgba(17, 24, 39, 0.6);
  font-weight: 900;
}

.dq-btns {
  display: flex;
  gap: 6px;
}

.dq-icon-btn {
  border-radius: 12px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* widths */
.dq-w-90 {
  width: 90px;
}
.dq-w-110 {
  width: 110px;
}

/* switches */
.dq-switch {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dq-switch-input {
  width: 42px;
  height: 22px;
}
.dq-switch-label {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.7);
}

/* action button */
.dq-action-btn {
  border-radius: 14px;
  padding: 12px 14px;
  font-weight: 900;
}

/* ========== CKEDITOR (prevent infinite height) ========== */
:deep(.ck-editor__editable_inline) {
  min-height: 180px;
  max-height: 40vh;
  overflow-y: auto;
  border-radius: 14px;
}

/* ========== QUICK SUMMARY KPI GRID (RIGHT SIDE) ========== */
.dq-kpi-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.dq-kpi {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  padding: 12px;
  background: rgba(248, 250, 252, 0.65);
}

.dq-kpi-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.dq-kpi-label {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.65);
  font-size: 0.85rem;
}

.dq-kpi-val {
  font-weight: 950;
  font-size: 1.25rem;
  color: rgba(17, 24, 39, 0.92);
}

.dq-kpi-ic {
  width: 36px;
  height: 36px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.dq-kpi-ic-blue {
  background: rgba(37, 99, 235, 0.12);
  color: rgba(37, 99, 235, 1);
}
.dq-kpi-ic-green {
  background: rgba(16, 185, 129, 0.12);
  color: rgba(5, 150, 105, 1);
}

/* divider (used in summary too) */
.dq-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
  margin: 12px 0;
}

/* ========== DELETE MODAL ========== */
.dq-modal-delete {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 30px 80px rgba(16, 24, 40, 0.18);
}

.dq-del-ic {
  width: 62px;
  height: 62px;
  border-radius: 20px;
  background: rgba(220, 38, 38, 0.12);
  color: rgba(220, 38, 38, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  font-size: 26px;
}

/* ========== MOBILE ========== */
@media (max-width: 576px) {
  :deep(#add_task .modal-dialog) {
    height: calc(100vh - 1.5rem) !important;
    margin: 0.75rem auto !important;
  }

  .dq-layout {
    padding: 10px;
  }
}
</style>
